// 「새로운 레이블리」 환영 여정 — 순수 규칙(IO 없음). 기획 docs/ops/new-laeebly-welcome-plan.md §4·§6
//
//   normalizeWelcomeState(raw)      RPC welcome_state() 응답 → 화면용 상태
//   shouldRedirectToWelcome(...)    로그인 뒤 /welcome 으로 보낼지 (UserLayout 의 WelcomeRedirect 가 쓴다)
//   buildConsentItems(state, now)   챕터 5 「확인하고 동의하기」 항목 — 무엇을 체크로 받고, 무엇이 잠겨 있고, 무엇이 이미 끝났나
//   readForcedWelcome(search)       ?welcome=preview — 기록 없이 화면만(QA · 어드민 미리보기)
//
// 🔒 원칙
//   · 동의(원문 해시)는 기존 게이트와 같은 표·같은 원문 — terms/privacy 는 policy 표 현재 버전(v3.5 · 2026-09-22 시행)으로 받는다.
//     LEGAL_V3.effectiveFrom 전이면 잠김 줄. age_confirm 은 행이 생기는 10/1 까지 「10월 1일부터」 잠김 줄.
//   · 유료 전환 동의(paid_change)는 창(10/1~10/31) 안에서만 — 창을 당기지 않는다(전자상거래법 §13⑥).
//   · 숏폼 정책은 policy 표에 행이 있으면 보인다(is_active 는 기존 팝업의 스위치 — 이 페이지는 welcome_2026 으로 잠김). 원문 초안 확정은 welcome 켜기 전 선행.
//   · 대리 접속·Admin·Platform·권리사 단독·파트너 단독은 대상이 아니다. role 이 비어 있으면 대상(실DB 2026-09-29: 363명 · 61%).
//     CUT 등급·비계약은 UserContext·ProtectedRoute 가 /total 로 보내고 /total 은 제외 경로라 자연히 빠진다.
import { consentStateFor, latestConsentByPolicy, needsPrompt } from "./policyConsentRules";
import { isLegalV3Effective, isPaidChangeConsentWindow } from "./legalDocuments";
import { normalizeRolloutNoticeState, rolloutSegment } from "./tierRolloutNotice";

export const WELCOME_VERSION = "2026-10";
export const WELCOME_QUERY_PARAM = "welcome";
export const WELCOME_EXCLUDED_PREFIXES = ["/welcome", "/terms-of-service", "/privacy-policy", "/policies", "/withdrawal", "/login", "/signup", "/onboarding", "/total"];

/**
 * 환영 페이지 대상(클라이언트) — 🔒 서버 welcome_state() 2판(마이그레이션 20260929150000)과 같은 규칙이어야 한다.
 *   admin·platform 제외 · 권리사 단독(Copyrights 만) 제외 · 파트너 단독(Partner 만) 제외 · 그 밖(role 비어 있음 포함)은 대상.
 *   policyConsentRules.isConsentTarget 은 role 이 비면 false 라서 쓰지 않는다 — 실DB(2026-09-29) role 빈 회원 363명(61%)이
 *   서버는 대상으로 보는데 화면이 안 보내는 사고가 최종 점검에서 잡혔다(대상 522명 중 159명만 이동할 뻔).
 */
export function isWelcomeTarget(role) {
  const r = String(role || "").toLowerCase();
  if (r.includes("admin") || r.includes("platform")) return false;
  if (r.includes("copyrights") && !r.includes("creator")) return false;
  if (r.includes("partner") && !r.includes("creator")) return false;
  return true;
}

export function normalizeWelcomeState(raw) {
  if (!raw || typeof raw !== "object") return null;
  const tier = raw.tier ? normalizeRolloutNoticeState(raw.tier) : null;
  return {
    enabled: raw.enabled === true,
    version: raw.version || WELCOME_VERSION,
    target: raw.target === true,
    role: raw.role ?? null,
    acked: raw.acked === true,
    ackedAt: raw.acked_at ?? null,
    introSeen: raw.intro_seen === true,
    tier,
    segment: tier ? rolloutSegment(tier) : "none",
    billing: raw.billing ?? null,
    policies: Array.isArray(raw.policies) ? raw.policies : [],
    consents: Array.isArray(raw.consents) ? raw.consents : [],
    shortformChannel: raw.shortform_channel === true,
    platformNews: Array.isArray(raw.platform_news) ? raw.platform_news : [],
  };
}

/** 로그인 뒤 홈 대신 /welcome 으로 보낼지. 조회 실패(state null)면 절대 보내지 않는다 — 안내가 앱을 막으면 안 된다. */
export function shouldRedirectToWelcome(state, { role, pathname = "/", impersonating = false } = {}) {
  if (!state || !state.enabled) return false;
  if (!state.target || !isWelcomeTarget(role)) return false;
  if (impersonating) return false;
  if (state.acked) return false;
  if (WELCOME_EXCLUDED_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`))) return false;
  return true;
}

/** Admin 은 스위치와 무관하게 /welcome 을 직접 열어 미리 본다 */
export function canPreviewWelcome(role) {
  return /admin/i.test(String(role || ""));
}

export function readForcedWelcome(search) {
  try {
    const v = new URLSearchParams(search || "").get(WELCOME_QUERY_PARAM);
    return v === "preview" ? "preview" : null;
  } catch {
    return null;
  }
}

const findPolicy = (state, key) => (state?.policies || []).find((p) => p.key === key) || null;

/**
 * 챕터 5 항목. 각 항목: { key, kind: 'consent'|'confirm', status: 'pending'|'locked'|'done'|'hidden', policy, chapter, lockReason }
 *   pending → 체크박스 · locked → 잠김 줄(이유 표시) · done → 이미 끝남(체크 표시) · hidden → 그리지 않음
 */
export function buildConsentItems(state, { now = new Date() } = {}) {
  const latest = latestConsentByPolicy(state?.consents || []);
  const legalOn = isLegalV3Effective(now);
  const items = [];

  // 1~3 약관 · 방침 · 만 14세 — policy 현재 버전 기준. 10/1 전에는 잠김.
  for (const key of ["terms", "privacy", "age_confirm"]) {
    const policy = findPolicy(state, key);
    if (!policy || !policy.is_active) {
      // policy 표에 없거나 꺼진 문서 — 약관·방침은 그리지 않고, 만 14세는 「10월 1일부터 동의를 받아요」 잠김 줄로 보여 준다
      // (실DB 에 age_confirm 행은 10/1 activate-legal-v3.sql 이 넣는다 · 대표 2026-09-23 「시안에 있던 항목은 다 보이게」)
      const kind = key === "age_confirm" ? "confirm" : "consent";
      items.push({ key, kind, status: key === "age_confirm" ? "locked" : "hidden", policy: null, chapter: 3, lockReason: key === "age_confirm" ? "legal_from" : null });
      continue;
    }
    const st = consentStateFor(policy, latest[key]);
    // 🔒 2026-09-29: 이관(legacy) 동의·옛 버전 동의도 여기서는 「다시 받는다」 — 기존 PolicyConsentGate 의 needsPrompt 는 legacy 를 완료로 보아
    //   회원 564명(2026-09-27 실DB)이 v3.5 재동의를 한 번도 받지 못했다. 환영 페이지는 현재 버전 동의(agreed)만 완료로 본다.
    if (policy.is_required && st !== "agreed") {
      items.push({ key, kind: key === "age_confirm" ? "confirm" : "consent", status: legalOn ? "pending" : "locked", policy, chapter: 3, lockReason: legalOn ? null : "legal_from" });
    } else {
      // 현재 버전에 이미 동의(또는 필수가 아닌 정책)
      items.push({ key, kind: key === "age_confirm" ? "confirm" : "consent", status: legalOn ? "done" : "locked", policy, chapter: 3, lockReason: legalOn ? null : "legal_from" });
    }
  }

  // 4 숏폼 수익 배분 정책 — policy 표에 행이 있으면 보여 주고 동의도 받는다(is_active 와 무관 · 대표 2026-09-23).
  //   is_active 는 기존 PolicyConsentGate(전 크리에이터 팝업)의 스위치이고, 이 페이지는 welcome_2026 스위치로 따로 잠겨 있다.
  //   🔒 정책 원문은 아직 초안(법무 검토 전) — welcome 스위치를 켜기 전에 원문 확정이 먼저다(docs/ops/naver-clip-policy-draft.md).
  {
    const policy = findPolicy(state, "shortform_revenue");
    if (policy) {
      // needsPrompt 는 is_active/is_required 를 보지만 여기서는 「현재 버전에 동의했나」만 본다(꺼진 정책도 미리 받는다)
      const st = consentStateFor(policy, latest.shortform_revenue);
      items.push({ key: "shortform_revenue", kind: "consent", status: st === "agreed" ? "done" : "pending", policy, chapter: 4, lockReason: null });
    } else {
      items.push({ key: "shortform_revenue", kind: "consent", status: "hidden", policy: null, chapter: 4, lockReason: null });
    }
  }

  // 5 등급제 시행 안내 확인 — tier_rollout_ack
  items.push({ key: "tier", kind: "confirm", status: state?.tier?.acked ? "done" : "pending", policy: null, chapter: 2, lockReason: null });

  // 6 유료 전환 동의 — 유료 등급 + 10/1~10/31 창
  {
    const policy = findPolicy(state, "paid_change");
    const paidSegment = state?.segment === "paid" && Number(state?.tier?.monthlyFee || 0) > 0;
    if (!paidSegment) {
      items.push({ key: "paid_change", kind: "consent", status: "hidden", policy, chapter: 2, lockReason: null });
    } else if (!isPaidChangeConsentWindow(now) || !policy || !policy.is_active) {
      items.push({ key: "paid_change", kind: "consent", status: "locked", policy, chapter: 2, lockReason: "paid_window" });
    } else {
      const st = consentStateFor(policy, latest.paid_change);
      items.push({ key: "paid_change", kind: "consent", status: needsPrompt(policy, st) ? "pending" : "done", policy, chapter: 2, lockReason: null });
    }
  }

  // 7 협업 약정 · 플랫폼 정책 · 개편 안내를 읽었어요 — welcome_ack
  items.push({ key: "read", kind: "confirm", status: state?.acked ? "done" : "pending", policy: null, chapter: 5, lockReason: null });

  return items;
}

/** 「동의하고 새 레이블리 시작」 을 누를 수 있나 — pending 항목이 전부 체크됐을 때 */
export function canFinishWelcome(items, checked) {
  const pending = (items || []).filter((i) => i.status === "pending");
  if (pending.length === 0) return false;
  return pending.every((i) => checked && checked[i.key] === true);
}

/** ack_welcome 에 넘길 동의 목록 — 체크된 정책 항목만 */
export function consentPayload(items, checked) {
  return (items || [])
    .filter((i) => i.status === "pending" && i.policy && checked && checked[i.key] === true)
    .map((i) => ({ key: i.key, version: i.policy.version }));
}
